import { useState, useEffect } from 'react'
import { customerApi, authApi } from '../services/api'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  KeyIcon,
  CloudArrowUpIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const AGENT_COLORS = ['#2D6A4F', '#6366f1', '#a855f7', '#f59e0b', '#ef4444', '#06b6d4']
const LEAD_STATUS_COLORS = {
  new: '#6366f1',
  contacted: '#f59e0b',
  qualified: '#06b6d4',
  converted: '#10b981',
  lost: '#ef4444',
}

export default function OverviewDashboard({ user, apiKey, knowledgeBases, onNavigate }) {
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState(null)
  const [conversationsData, setConversationsData] = useState([])
  const [agentDist, setAgentDist] = useState(null)
  const [leadStats, setLeadStats] = useState(null)
  const [topQuestions, setTopQuestions] = useState(null)
  const [peakHours, setPeakHours] = useState(null)
  const [quality, setQuality] = useState(null)
  const [usageSummary, setUsageSummary] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [trialInfo, setTrialInfo] = useState(null)
  const [period, setPeriod] = useState('all')

  // Convert period string to days for usage endpoints (0 = all time)
  const periodToDays = (p) => {
    if (p === '7d') return 7
    if (p === '30d') return 30
    if (p === '90d') return 90
    return 0
  }

  const kbCount = Array.isArray(knowledgeBases) ? knowledgeBases.length : 0

  const setupSteps = [
    { label: 'API Key', done: !!apiKey },
    { label: 'Knowledge Base', done: kbCount > 0 },
    { label: 'First Chat', done: (overview?.total_conversations || 0) > 0 },
  ]
  const setupPercent = Math.round(
    (setupSteps.filter((s) => s.done).length / setupSteps.length) * 100
  )

  useEffect(() => {
    fetchAllData()
  }, [period])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      const days = periodToDays(period)
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const results = await Promise.allSettled([
        customerApi.get(`/customer/analytics/overview?period=${period}`),
        customerApi.get(`/customer/analytics/conversations-over-time?period=${period}`),
        customerApi.get(`/customer/analytics/agent-distribution?period=${period}`),
        customerApi.get(`/customer/analytics/top-questions?period=${period}&limit=5`),
        customerApi.get(`/customer/analytics/peak-hours?period=${period}&tz=${tz}`),
        customerApi.get(`/customer/analytics/response-quality?period=${period}`),
        customerApi.get(`/customer/usage/summary?days=${days}`),
        customerApi.get('/customer/leads/stats'),
        authApi.get('/auth/dashboard'),
      ])

      if (results[0].status === 'fulfilled') setOverview(results[0].value.data)
      if (results[1].status === 'fulfilled') setConversationsData(results[1].value.data?.data || [])
      if (results[2].status === 'fulfilled') setAgentDist(results[2].value.data)
      if (results[3].status === 'fulfilled') setTopQuestions(results[3].value.data)
      if (results[4].status === 'fulfilled') setPeakHours(results[4].value.data)
      if (results[5].status === 'fulfilled') setQuality(results[5].value.data)
      if (results[6].status === 'fulfilled') setUsageSummary(results[6].value.data)
      if (results[7].status === 'fulfilled') setLeadStats(results[7].value.data)
      if (results[8].status === 'fulfilled') {
        const sub = results[8].value.data?.subscription
        if (sub?.trial_days_remaining !== null && sub?.trial_days_remaining !== undefined) {
          setTrialInfo({ daysRemaining: sub.trial_days_remaining, endDate: sub.trial_end_date })
        }
      }

      setLastUpdated(new Date())
    } catch (err) {
      console.error('Failed to fetch overview data:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const formatCost = (cost) => {
    if (!cost || cost === 0) return '$0.00'
    return '$' + cost.toFixed(4)
  }

  // ============================================================
  // Loading skeleton
  // ============================================================
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-28 bg-slate-100 rounded-2xl animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 h-72 bg-slate-100 rounded-xl animate-pulse" />
          <div className="h-72 bg-slate-100 rounded-xl animate-pulse" />
        </div>
      </div>
    )
  }

  // ============================================================
  // Render
  // ============================================================
  const totalConvs = overview?.total_conversations || 0
  const totalMsgs = overview?.total_messages || 0
  const avgPerConv = overview?.avg_messages_per_conversation || 0
  const successRate = quality?.success_rate ?? 0
  const totalLeads = leadStats?.total || 0
  const monthlyCost = usageSummary?.total_cost || 0
  const convsChange = overview?.comparison?.conversations_change
  const msgsChange = overview?.comparison?.messages_change

  const pieData = (agentDist?.agents || []).map((a) => ({ name: a.name, value: a.count }))

  const leadPipeline = leadStats?.by_status || {}
  const leadPipelineData = Object.entries(leadPipeline)
    .filter(([, v]) => v > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: LEAD_STATUS_COLORS[status] || '#94a3b8',
    }))

  return (
    <div className="space-y-5">
      {/* Trial Countdown Banner */}
      {user?.subscriptionPlan === 'starter' && trialInfo && (
        <div className={"rounded-xl p-4 flex items-center justify-between " + (trialInfo.daysRemaining <= 2 ? 'bg-red-50 border border-red-200' : trialInfo.daysRemaining <= 4 ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200')}>
          <div className="flex items-center space-x-3">
            <div className={"flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold " + (trialInfo.daysRemaining <= 2 ? 'bg-red-100 text-red-700' : trialInfo.daysRemaining <= 4 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700')}>
              {trialInfo.daysRemaining}
            </div>
            <div>
              <p className={"font-semibold text-sm " + (trialInfo.daysRemaining <= 2 ? 'text-red-800' : trialInfo.daysRemaining <= 4 ? 'text-amber-800' : 'text-blue-800')}>
                {trialInfo.daysRemaining === 0
                  ? 'Your free trial expires today!'
                  : trialInfo.daysRemaining === 1
                    ? '1 day remaining on your free trial'
                    : trialInfo.daysRemaining + ' days remaining on your free trial'}
              </p>
              <p className={"text-xs mt-0.5 " + (trialInfo.daysRemaining <= 2 ? 'text-red-600' : trialInfo.daysRemaining <= 4 ? 'text-amber-600' : 'text-blue-600')}>
                {trialInfo.endDate ? 'Expires ' + new Date(trialInfo.endDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
              </p>
            </div>
          </div>
          <a
            href="/subscribe?plan=growth_6month"
            className={"px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors " + (trialInfo.daysRemaining <= 2 ? 'bg-red-600 hover:bg-red-700' : trialInfo.daysRemaining <= 4 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700')}
          >
            Upgrade Now
          </a>
        </div>
      )}

      {/* Welcome banner + refresh */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">
              Welcome back, {user?.fullName || user?.email?.split('@')[0] || 'there'}!
            </h2>
            <p className="text-brand-100 text-sm">
              Here's your {period === 'all' ? 'all-time' : period === '7d' ? '7-day' : period === '30d' ? '30-day' : '90-day'} performance snapshot.
              {lastUpdated && (
                <span className="ml-2 text-brand-200 text-xs">
                  Updated {lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="flex bg-white/10 rounded-xl p-1">
              {[
                { value: '7d', label: '7d' },
                { value: '30d', label: '30d' },
                { value: '90d', label: '90d' },
                { value: 'all', label: 'All time' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setPeriod(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    period === opt.value
                      ? 'bg-white text-brand-700'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={fetchAllData}
              className="flex items-center space-x-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-sm font-medium transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Metric cards row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard
          icon={ChatBubbleLeftRightIcon}
          label="Conversations"
          value={totalConvs}
          change={convsChange}
          color="brand"
        />
        <MetricCard
          icon={EnvelopeIcon}
          label="Messages"
          value={totalMsgs}
          change={msgsChange}
          color="blue"
        />
        <MetricCard
          icon={SparklesIcon}
          label="Avg / Conv"
          value={avgPerConv.toFixed(1)}
          color="purple"
        />
        <MetricCard
          icon={CpuChipIcon}
          label="Success Rate"
          value={successRate > 0 ? successRate.toFixed(0) + '%' : '--'}
          color="emerald"
        />
        <MetricCard
          icon={UserGroupIcon}
          label="Leads"
          value={totalLeads}
          sub={leadStats?.this_month ? `${leadStats.this_month} this month` : undefined}
          color="amber"
        />
        <MetricCard
          icon={CurrencyDollarIcon}
          label="AI Cost"
          value={formatCost(monthlyCost)}
          color="rose"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Conversations over time */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Conversations &amp; Messages</h3>
          {conversationsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={conversationsData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  labelFormatter={formatDate}
                />
                <Line type="monotone" dataKey="conversations" stroke="#2D6A4F" strokeWidth={2} dot={false} name="Conversations" />
                <Line type="monotone" dataKey="messages" stroke="#6366f1" strokeWidth={2} dot={false} name="Messages" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <ChatBubbleLeftRightIcon className="h-10 w-10 mb-2 text-slate-300" />
              <p className="text-sm">No conversation data yet</p>
              <button onClick={() => onNavigate('chat')} className="mt-2 text-xs text-brand-600 hover:underline">
                Start chatting to see trends
              </button>
            </div>
          )}
        </div>

        {/* Agent distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Agent Routing</h3>
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={AGENT_COLORS[i % AGENT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                    formatter={(value, name) => [`${value}`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1.5">
                {pieData.map((entry, i) => (
                  <div key={entry.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: AGENT_COLORS[i % AGENT_COLORS.length] }} />
                      <span className="text-slate-600">{entry.name}</span>
                    </div>
                    <span className="font-medium text-slate-900">{entry.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
              <CpuChipIcon className="h-10 w-10 mb-2 text-slate-300" />
              <p className="text-sm">No agent data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom row: Top Questions + Lead Pipeline + Peak Hours */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top Questions */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Top Questions</h3>
            <QuestionMarkCircleIcon className="h-4 w-4 text-slate-400" />
          </div>
          {topQuestions?.questions?.length > 0 ? (
            <div className="space-y-3">
              {topQuestions.questions.slice(0, 5).map((q, i) => (
                <div key={i} className="group">
                  <div className="flex items-start justify-between">
                    <p className="text-xs text-slate-700 line-clamp-2 flex-1 mr-2">{q.question}</p>
                    <span className="text-xs font-semibold text-slate-900 tabular-nums flex-shrink-0">{q.count}</span>
                  </div>
                  <div className="mt-1.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-500 rounded-full transition-all duration-500"
                      style={{ width: `${q.percentage || 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-36 text-slate-400">
              <QuestionMarkCircleIcon className="h-8 w-8 mb-2 text-slate-300" />
              <p className="text-xs">Questions will appear here</p>
            </div>
          )}
        </div>

        {/* Lead Pipeline */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Lead Pipeline</h3>
            {leadStats?.conversion_rate > 0 && (
              <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                {leadStats.conversion_rate.toFixed(1)}% conversion
              </span>
            )}
          </div>
          {leadPipelineData.length > 0 ? (
            <div className="space-y-3">
              {leadPipelineData.map((entry) => (
                <div key={entry.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center space-x-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-xs text-slate-600">{entry.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-slate-900">{entry.value}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${totalLeads > 0 ? (entry.value / totalLeads) * 100 : 0}%`, backgroundColor: entry.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-36 text-slate-400">
              <UserGroupIcon className="h-8 w-8 mb-2 text-slate-300" />
              <p className="text-xs">No leads captured yet</p>
              <button onClick={() => onNavigate('leads')} className="mt-1 text-xs text-brand-600 hover:underline">
                View lead settings
              </button>
            </div>
          )}
        </div>

        {/* Peak Activity Hours */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Peak Hours</h3>
            {peakHours?.peak_label && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Peak: {peakHours.peak_label}
              </span>
            )}
          </div>
          {peakHours?.hours?.some((h) => h.conversations > 0) ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={peakHours.hours} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 8, fill: '#94a3b8' }}
                  interval={2}
                />
                <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  formatter={(value) => [`${value} conversations`]}
                />
                <Bar dataKey="conversations" radius={[3, 3, 0, 0]}>
                  {peakHours.hours.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.hour === peakHours.peak_hour ? '#f59e0b' : '#e2e8f0'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-36 text-slate-400">
              <ClockIcon className="h-8 w-8 mb-2 text-slate-300" />
              <p className="text-xs">Activity hours will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick actions + setup progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <QuickActionBtn
              icon={KeyIcon}
              label="API Keys"
              sub="Manage & copy"
              color="brand"
              onClick={() => onNavigate('api-keys')}
            />
            <QuickActionBtn
              icon={CloudArrowUpIcon}
              label="Add KB"
              sub="Scrape or upload"
              color="blue"
              onClick={() => onNavigate('knowledge-base')}
            />
            <QuickActionBtn
              icon={ChatBubbleLeftRightIcon}
              label="AI Chat"
              sub="Test your bot"
              color="purple"
              onClick={() => onNavigate('chat')}
            />
            <QuickActionBtn
              icon={DocumentTextIcon}
              label="Analytics"
              sub="Full report"
              color="emerald"
              onClick={() => onNavigate('analytics')}
            />
          </div>
        </div>

        {/* Setup progress */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Setup Progress</h3>
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                <circle
                  cx="40" cy="40" r="32" fill="none" stroke="#059669" strokeWidth="6"
                  strokeDasharray={`${setupPercent * 2.01} 201`}
                  strokeLinecap="round"
                  className="transition-all duration-700"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-slate-900">
                {setupPercent}%
              </span>
            </div>
          </div>
          <div className="space-y-2">
            {setupSteps.map((step) => (
              <div key={step.label} className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${step.done ? 'bg-emerald-500' : 'bg-slate-200'}`}>
                  {step.done && <CheckIcon className="h-2.5 w-2.5 text-white" />}
                </div>
                <span className={`text-xs ${step.done ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Sub-components
// ============================================================

function MetricCard({ icon: Icon, label, value, change, sub, color = 'brand' }) {
  const bgMap = {
    brand: 'bg-brand-50',
    blue: 'bg-blue-50',
    purple: 'bg-purple-50',
    emerald: 'bg-emerald-50',
    amber: 'bg-amber-50',
    rose: 'bg-rose-50',
  }
  const iconMap = {
    brand: 'text-brand-600',
    blue: 'text-blue-600',
    purple: 'text-purple-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    rose: 'text-rose-600',
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg ${bgMap[color]} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${iconMap[color]}`} />
        </div>
        {change !== undefined && change !== null && (
          <span
            className={`flex items-center space-x-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
              change >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'
            }`}
          >
            {change >= 0 ? (
              <ArrowTrendingUpIcon className="h-2.5 w-2.5" />
            ) : (
              <ArrowTrendingDownIcon className="h-2.5 w-2.5" />
            )}
            <span>{Math.abs(change).toFixed(0)}%</span>
          </span>
        )}
      </div>
      <p className="text-xl font-bold text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  )
}

function QuickActionBtn({ icon: Icon, label, sub, color, onClick }) {
  const colorMap = {
    brand: { bg: 'bg-brand-100', hoverBg: 'hover:bg-brand-50', hoverBorder: 'hover:border-brand-300', icon: 'text-brand-700', hoverIcon: 'group-hover:bg-brand-200' },
    blue: { bg: 'bg-blue-100', hoverBg: 'hover:bg-blue-50', hoverBorder: 'hover:border-blue-300', icon: 'text-blue-700', hoverIcon: 'group-hover:bg-blue-200' },
    purple: { bg: 'bg-purple-100', hoverBg: 'hover:bg-purple-50', hoverBorder: 'hover:border-purple-300', icon: 'text-purple-700', hoverIcon: 'group-hover:bg-purple-200' },
    emerald: { bg: 'bg-emerald-100', hoverBg: 'hover:bg-emerald-50', hoverBorder: 'hover:border-emerald-300', icon: 'text-emerald-700', hoverIcon: 'group-hover:bg-emerald-200' },
  }
  const c = colorMap[color] || colorMap.brand

  return (
    <button
      onClick={onClick}
      className={`flex items-center space-x-3 p-3 rounded-xl border border-slate-200 ${c.hoverBorder} ${c.hoverBg} transition-all group`}
    >
      <div className={`w-9 h-9 rounded-lg ${c.bg} flex items-center justify-center ${c.hoverIcon} transition-colors`}>
        <Icon className={`h-4 w-4 ${c.icon}`} />
      </div>
      <div className="text-left">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="text-[10px] text-slate-500">{sub}</p>
      </div>
    </button>
  )
}
